/* eslint-disable @eslint-react/no-array-index-key --
   The lists here (rendered text fragments, member records, aliases, evidence,
   audit entries) have no stable id of their own, so the index is used as a
   tiebreaker inside composite keys. Pre-dates the graph deep-link change
   below; rewriting the keys would risk reconciliation regressions in this
   modal for no functional gain. */
import { Modal, Select, Tag, Input, Spin, Tabs, Tooltip } from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  ShareAltOutlined,
  SendOutlined,
  LinkOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";

import AvatarWithInitials from "@/components/UI/AvatarWithInitials";
import {
  fieldLabel,
  scoreColor,
  membersFromComparison,
} from "./diffModalUtils";

const { TextArea } = Input;

// ── Types ─────────────────────────────────────────────────────────────────────

export type ItemType =
  | "conflict"
  | "pre_mastering"
  | "pair_review"
  | "pi_review";

export type EvidenceEntry = {
  side: string;
  web_name: string | null;
  web_address: string | null;
  web_url: string | null;
  confidence: number | null;
};

export type SiblingPair = {
  id: string;
  label: string;
  confidence_pct: number | null;
};

export type PriorDecision = {
  decision: string | null;
  reason: string;
  actor: string | null;
  at: string | null;
};

/** One contributing raw/source record behind a golden site or golden person. */
export type ComparisonMember = {
  id: string;
  source_system: string | null;
  source_label: string;
  name: string;
  confidence_pct: number | null;
  confidence_tier?: string | null;
  scores?: Record<string, number>;
  // site members
  address?: string;
  city?: string | null;
  phone?: string | null;
  email?: string | null;
  institution?: string | null;
  department?: string | null;
  pi_name?: string | null;
  site_number?: string | null;
  irb_number?: string | null;
  external_org_id?: string | null;
  trial_id?: string | null;
  // investigator members
  role?: string | null;
  specialty?: string | null;
  sub_specialty?: string | null;
  npi?: string | null;
  nct_id?: string | null;
  country?: string | null;
};

/** Elected canonical values for the golden record on one side of a comparison. */
export type ComparisonGolden = {
  golden_id?: string;
  person_id?: string;
  name: string | null;
  address?: string;
  city?: string | null;
  country?: string | null;
  phone?: string | null;
  email?: string | null;
  institution?: string | null;
  department?: string | null;
  pi_name?: string | null;
  specialty?: string | null;
  npi?: string | null;
  golden_institution_id?: string | null;
  merge_count?: number | null;
  overall_confidence?: number | null;
  admission_tier?: string | null;
  recon_status?: string | null;
  source_systems?: string[];
  aliases?: string[];
};

export type ComparisonSide = {
  key: string;
  label: string;
  golden: ComparisonGolden | null;
  members: ComparisonMember[];
};

/** Full member detail behind the golden record(s) under review. */
export type MemberComparison = {
  entity: "site" | "investigator";
  mode: "single" | "pair";
  sides: ComparisonSide[];
  aliases?: string[];
  shared_identifiers?: Record<string, string>;
  conflicting_identifiers?: string[];
  evidence?: {
    raw_site_id: string | null;
    raw_site_name: string | null;
    ev_name: string | null;
  }[];
};

export type QueueItemDetail = {
  id: string;
  item_type: ItemType;
  entity_type: "site" | "investigator";
  entity_key: string;
  entity_display_name: string;
  nct_id: string;
  name_as_registered: string;
  source: string | null;
  issue_type: string;
  issue_summary: string;
  proposed_canonical: string | null;
  confidence_pct: number | null;
  confidence_lower?: number | null;
  confidence_upper?: number | null;
  weighted_score?: number | null;
  accept_basket?: string | null;
  due_date?: string | null;
  assigned_to?: string | null;
  escalation_reason?: string | null;
  priority: "high" | "medium" | "low" | null;
  status: string | null;
  created_at: string | null;
  tags: string[];
  raw_fields: Record<string, string | null>;
  golden_fields: Record<string, string | null>;
  field_scores: Record<string, number>;
  details?: Record<string, unknown>;
  evidence?: EvidenceEntry[];
  siblings?: SiblingPair[];
  prior_decisions?: PriorDecision[];
  comparison?: MemberComparison | null;
  permissions?: { can_steward: boolean };
  pipeline_reltype?: string | null;
  left_label?: string;
  right_label?: string;
};

export type Comment = {
  id: string;
  author: string;
  timestamp: string;
  text: string;
};

export type AuditEntry = {
  action: string;
  action_value: string | null;
  notes: string;
  performed_by: string | null;
  performed_at: string;
  writeback_target?: string | null;
  writeback_status?:
    | "applied"
    | "failed"
    | "disabled"
    | "applied_in_run"
    | "rejected_by_pipeline"
    | null;
  writeback_detail?: string;
};

/** Decided statuses whose application awaits the next mastering run. */
const DECIDED_STATUSES = new Set(["resolved", "rejected", "dismissed"]);

// All 14 pre-mastering signals, in display order — absent ones render as "—".
const ALL_SIGNALS: Array<{ key: string; label: string }> = [
  { key: "name", label: "Site name" },
  { key: "address", label: "Address" },
  { key: "geo", label: "Geo distance" },
  { key: "institution", label: "Institution" },
  { key: "nct_overlap", label: "NCT overlap" },
  { key: "pi", label: "PI name" },
  { key: "pi_rarity", label: "PI rarity" },
  { key: "investigator_network", label: "Investigator network" },
  { key: "coordinator", label: "Coordinator" },
  { key: "phone", label: "Phone" },
  { key: "email", label: "Email domain" },
  { key: "irb", label: "IRB" },
  { key: "site_number", label: "Site number" },
  { key: "site_continuity", label: "Site continuity" },
];

const ISSUE_TYPE_LABELS: Record<string, string> = {
  name_variant: "Name variant",
  affiliation_variant: "Affiliation variant",
  address_mismatch: "Address mismatch",
  duplicate: "Duplicate record",
  role_conflict: "Role conflict",
  npi_unverified: "NPI unverified",
  field_conflict: "Field conflict",
  geo_conflict: "Geo conflict",
  split_risk: "Split risk",
  cross_fgid_dup: "Cross-ID duplicate",
};

const ISSUE_TYPE_COLORS: Record<string, { color: string; bg: string }> = {
  name_variant: { color: "#0C98D0", bg: "#D2EFF9" },
  affiliation_variant: { color: "#556C75", bg: "#E8EEF2" },
  address_mismatch: { color: "#B45309", bg: "#FEF3C7" },
  duplicate: { color: "#C2410C", bg: "#FEE2E2" },
  role_conflict: { color: "#B45309", bg: "#FEF9C3" },
  npi_unverified: { color: "#48636F", bg: "#E8EEF2" },
  field_conflict: { color: "#B45309", bg: "#FEF3C7" },
  geo_conflict: { color: "#3730A3", bg: "#EEF2FF" },
  split_risk: { color: "#C2410C", bg: "#FFF7ED" },
  cross_fgid_dup: { color: "#BE0303", bg: "#FEE2E2" },
};

const ITEM_TYPE_LABELS: Record<ItemType, string> = {
  conflict: "Conflict",
  pre_mastering: "Pre-mastering",
  pair_review: "Pair review",
  pi_review: "PI dedup",
};

const ACTION_LABELS: Record<string, string> = {
  approved: "Approved",
  rejected: "Rejected",
  skipped: "Skipped",
  accepted: "Accepted match",
  not_a_match: "Not a match",
  deferred: "Deferred",
  not_an_issue: "Not an issue",
  resolved: "Resolved",
  dismissed: "Dismissed",
  escalated: "Escalated",
  status_changed: "Status changed",
  priority_changed: "Priority changed",
  note_added: "Note added",
  reopened: "Reopened",
  pipeline_applied: "Applied by pipeline",
};

const STATUS_BADGES: Record<
  string,
  { bg: string; text: string; label: string }
> = {
  pending: { bg: "#E8EEF2", text: "#556C75", label: "Pending" },
  under_review: { bg: "#D2EFF9", text: "#06709B", label: "Under Review" },
  resolved: { bg: "#DCFCE7", text: "#15803D", label: "Resolved" },
  rejected: { bg: "#FEE2E2", text: "#BE0303", label: "Rejected" },
  dismissed: { bg: "#F1EFE8", text: "#5F5E5A", label: "Dismissed" },
  open: { bg: "#E8EEF2", text: "#556C75", label: "Open" },
};

export type MasteryAction =
  | "accepted"
  | "not_a_match"
  | "deferred"
  | "not_an_issue"
  | "resolved"
  | "dismissed"
  | "reopened";

function fmt(iso: string) {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function renderText(text: string) {
  return text.split(/(@\S+)/g).map((part, i) =>
    part.startsWith("@") ? (
      <span key={i} className="font-semibold text-[#0C98D0]">
        {part}
      </span>
    ) : (
      part
    ),
  );
}

function isOverdue(item: QueueItemDetail): boolean {
  if (!item.due_date) return false;
  const open = ["pending", "under_review", "in_progress", "open"];
  return (
    open.includes(item.status ?? "pending") &&
    new Date(item.due_date) < new Date()
  );
}

/** Flatten an arbitrary JSON payload into label/value rows for display. */
function flattenDetails(
  value: unknown,
  prefix = "",
): Array<{ key: string; value: string }> {
  if (value == null) return [];
  if (Array.isArray(value)) {
    return value.flatMap((v, i) => flattenDetails(v, `${prefix}[${i + 1}]`));
  }
  if (typeof value === "object") {
    return Object.entries(value as Record<string, unknown>).flatMap(([k, v]) =>
      flattenDetails(v, prefix ? `${prefix} · ${k}` : k),
    );
  }
  return [{ key: prefix || "value", value: String(value) }];
}

/** One column of the side-by-side field comparison. */
// ── Footer actions by item type ───────────────────────────────────────────────

type FooterAction = {
  action: MasteryAction | "skip";
  label: string;
  kind: "ghost" | "danger" | "primary";
};

const FOOTER_ACTIONS: Record<ItemType, FooterAction[]> = {
  conflict: [
    { action: "skip", label: "Skip", kind: "ghost" },
    { action: "dismissed", label: "Dismiss", kind: "danger" },
    { action: "resolved", label: "Resolve", kind: "primary" },
  ],
  pre_mastering: [
    { action: "deferred", label: "Defer", kind: "ghost" },
    { action: "not_an_issue", label: "Not an Issue", kind: "ghost" },
    { action: "not_a_match", label: "Not a Match", kind: "danger" },
    { action: "accepted", label: "Accept Match", kind: "primary" },
  ],
  pair_review: [
    { action: "deferred", label: "Defer", kind: "ghost" },
    { action: "not_a_match", label: "Keep Separate", kind: "danger" },
    { action: "accepted", label: "Merge", kind: "primary" },
  ],
  pi_review: [
    { action: "deferred", label: "Defer", kind: "ghost" },
    { action: "not_an_issue", label: "Not an Issue", kind: "ghost" },
    { action: "not_a_match", label: "Not a Match", kind: "danger" },
    { action: "accepted", label: "Accept Match", kind: "primary" },
  ],
};

const FOOTER_BUTTON_CLASSES: Record<FooterAction["kind"], string> = {
  ghost:
    "px-3 py-1.5 text-[13px] rounded border border-[#D5DADC] bg-white hover:bg-[#F5F7F8] transition-colors",
  danger:
    "flex items-center gap-1.5 px-3 py-1.5 text-[13px] rounded border text-[#BE0303] border-[#BE0303] hover:bg-[#FEE2E2] transition-colors",
  primary:
    "flex items-center gap-1.5 px-3 py-1.5 text-[13px] rounded bg-[#2B4752] text-white hover:bg-[#1F3540] transition-colors",
};

// ── DiffModal ─────────────────────────────────────────────────────────────────

export type DiffModalProps = {
  item: QueueItemDetail | null;
  loading: boolean;
  comments: Comment[];
  auditHistory: AuditEntry[];
  auditHistoryLoading: boolean;
  onClose: () => void;
  onOpenItem: (itemId: string) => void;
  draft: string;
  onDraftChange: (v: string) => void;
  onSubmitComment: () => void;
  onPriorityChange: (v: string | null) => void;
  onAction: (action: MasteryAction, opts?: { keepOpen?: boolean }) => void;
  userFullName: string;
};

export default function DiffModal({
  item,
  loading,
  comments,
  auditHistory,
  auditHistoryLoading,
  onClose,
  onOpenItem,
  draft,
  onDraftChange,
  onSubmitComment,
  onPriorityChange,
  onAction,
  userFullName,
}: DiffModalProps) {
  if (loading || !item) {
    return (
      <Modal open onCancel={onClose} footer={null} width={480} centered>
        <div className="flex items-center justify-center py-16">
          <Spin />
        </div>
      </Modal>
    );
  }

  const normalizedType = item.issue_type?.toLowerCase().replace(/\s+/g, "_");
  const colors = ISSUE_TYPE_COLORS[normalizedType] ?? {
    color: "#556C75",
    bg: "#E8EEF2",
  };

  const rawFields = item.raw_fields ?? {};
  const goldenFields = item.golden_fields ?? {};
  const hasGolden =
    Object.keys(goldenFields).some((k) => goldenFields[k] != null) ||
    item.proposed_canonical !== null;

  const allKeys = Array.from(
    new Set([...Object.keys(rawFields), ...Object.keys(goldenFields)]),
  );

  const comparisonRows =
    allKeys.length > 0
      ? allKeys.map((key) => ({
          key,
          label: fieldLabel(key),
          left: rawFields[key] ?? null,
          right: goldenFields[key] ?? null,
        }))
      : [
          {
            key: "name",
            label: "Name as registered",
            left: item.name_as_registered,
            right: item.proposed_canonical,
          },
        ];

  const fieldScores = item.field_scores ?? {};
  const overdue = isOverdue(item);
  const showSignalsTab = item.item_type === "pre_mastering";
  const comparison = item.comparison ?? null;
  const comparisonSides = comparison?.sides ?? [];
  const totalMembers = comparisonSides.reduce(
    (n, s) => n + (s.members?.length ?? 0),
    0,
  );
  const showMembersTab = totalMembers > 0;

  // Multi-member conflicts (e.g. COUNTRY_CONFLICT, where one item is raised per
  // disagreeing member) get a column per member instead of raw-vs-golden.
  const memberRecords = membersFromComparison(comparison);
  const useMulti = memberRecords.length >= 2;
  const multiKeys = Array.from(
    new Set([
      ...memberRecords.flatMap((m) => Object.keys(m.fields)),
      ...(hasGolden ? Object.keys(goldenFields) : []),
    ]),
  );
  const multiColumns = memberRecords.length + (hasGolden ? 1 : 0);
  const multiGrid = {
    display: "grid",
    gridTemplateColumns: `140px repeat(${multiColumns}, minmax(140px, 1fr))`,
  } as const;
  /** True when the members (and golden) disagree on this field. */
  const multiDiffers = (key: string): boolean => {
    const values = [
      ...memberRecords.map((m) => m.fields[key]),
      ...(hasGolden ? [goldenFields[key]] : []),
    ]
      .filter((v) => v != null && String(v).trim() !== "")
      .map((v) => String(v).toLowerCase().trim());
    return new Set(values).size > 1;
  };
  const evidence = item.evidence ?? [];
  const siblings = item.siblings ?? [];
  const priorDecisions = item.prior_decisions ?? [];
  // BR 59: destructive actions require the steward role (backend-decided).
  const canSteward = item.permissions?.can_steward ?? false;
  // Decision lifecycle: decided items await the next mastering run until a
  // pipeline_applied audit entry confirms application.
  const pipelineApplied = auditHistory.find(
    (e) => e.action === "pipeline_applied",
  );
  const awaitingRun =
    !pipelineApplied && DECIDED_STATUSES.has(item.status ?? "");

  // Deep link into the mastery graph, seeded on this record. The graph expands
  // one hop from the golden's members, so a pair review's counterpart shows up
  // in the same neighborhood. Investigator items have no site-graph equivalent.
  const graphHref =
    item.entity_type === "site" && item.entity_key
      ? `/portfolio/data/mastery?view=graph&seed=${encodeURIComponent(item.entity_key)}`
      : null;

  const detailsTab = (
    <div
      className="overflow-y-auto"
      style={{ background: "#EFF1F2", height: "100%" }}
    >
      <div
        className="mx-4 mt-4 mb-3 rounded overflow-hidden"
        style={{ border: "1px solid #D5DADC", background: "#fff" }}
      >
        {useMulti && (
          <div className="overflow-x-auto">
            <div
              style={{
                ...multiGrid,
                borderBottom: "1px solid #D5DADC",
                background: "#F5F7F8",
              }}
            >
              <div className="px-4 py-2.5" />
              {memberRecords.map((m, i) => (
                <div
                  key={`${m.label}-${i}`}
                  className="px-4 py-2.5"
                  style={{ borderLeft: "1px solid #D5DADC" }}
                >
                  <span
                    className="text-[11px] font-semibold uppercase tracking-wide block truncate"
                    style={{ color: "#556C75" }}
                    title={m.source ? `${m.source} · ${m.label}` : m.label}
                  >
                    {m.source ? `${m.source} · ` : ""}
                    {m.label}
                  </span>
                </div>
              ))}
              {hasGolden && (
                <div
                  className="px-4 py-2.5"
                  style={{ borderLeft: "1px solid #D5DADC" }}
                >
                  <span
                    className="text-[11px] font-semibold uppercase tracking-wide"
                    style={{ color: "#064C68" }}
                  >
                    {item.right_label ?? "Golden Record"}
                  </span>
                </div>
              )}
            </div>
            {multiKeys.map((key) => {
              const differs = multiDiffers(key);
              return (
                <div
                  key={key}
                  style={{
                    ...multiGrid,
                    borderBottom: "1px solid #D5DADC",
                    background: differs ? "rgba(12,152,208,0.04)" : "#fff",
                  }}
                >
                  <div
                    className="px-4 py-3 flex items-center gap-2"
                    style={{ background: "#FAFBFB" }}
                  >
                    <span
                      className="text-[11px] font-semibold uppercase tracking-widest"
                      style={{ color: "#556C75" }}
                    >
                      {fieldLabel(key)}
                    </span>
                    {differs && (
                      <span
                        className="w-1.5 h-1.5 rounded-full shrink-0"
                        style={{ background: "#0C98D0" }}
                      />
                    )}
                  </div>
                  {memberRecords.map((m, i) => {
                    const val = m.fields[key];
                    return (
                      <div
                        key={`${m.label}-${i}-${key}`}
                        className="px-4 py-3"
                        style={{
                          borderLeft: differs
                            ? "2px solid #0C98D0"
                            : "1px solid #D5DADC",
                        }}
                      >
                        {val == null ? (
                          <span
                            className="text-[13px] italic"
                            style={{ color: "#8FA8B2" }}
                          >
                            —
                          </span>
                        ) : (
                          <span
                            className="text-[13px]"
                            style={{
                              color: differs ? "#064C68" : "#162429",
                              fontWeight: differs ? 500 : 400,
                            }}
                          >
                            {val}
                          </span>
                        )}
                      </div>
                    );
                  })}
                  {hasGolden && (
                    <div
                      className="px-4 py-3"
                      style={{
                        borderLeft: differs
                          ? "2px solid rgba(12,152,208,0.4)"
                          : "1px solid #D5DADC",
                      }}
                    >
                      {goldenFields[key] == null ? (
                        <span
                          className="text-[13px] italic"
                          style={{ color: "#8FA8B2" }}
                        >
                          —
                        </span>
                      ) : (
                        <span
                          className="text-[13px]"
                          style={{
                            color: differs ? "#0C98D0" : "#162429",
                            fontWeight: differs ? 500 : 400,
                          }}
                        >
                          {goldenFields[key]}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {!useMulti && (
          <>
            <div
              className="grid grid-cols-[140px_1fr_1fr]"
              style={{
                borderBottom: "1px solid #D5DADC",
                background: "#F5F7F8",
              }}
            >
              <div className="px-4 py-2.5" />
              <div
                className="px-4 py-2.5"
                style={{ borderLeft: "1px solid #D5DADC" }}
              >
                <span
                  className="text-[11px] font-semibold uppercase tracking-wide"
                  style={{ color: "#556C75" }}
                >
                  {item.left_label ??
                    (item.entity_type === "site" ? "Site A" : "Investigator A")}
                </span>
              </div>
              <div
                className={`px-4 py-2.5 ${hasGolden ? "" : "opacity-40"}`}
                style={{ borderLeft: "1px solid #D5DADC" }}
              >
                <span
                  className="text-[11px] font-semibold uppercase tracking-wide"
                  style={{ color: "#064C68" }}
                >
                  {item.right_label ??
                    (item.entity_type === "site" ? "Site B" : "Investigator B")}
                </span>
              </div>
            </div>

            {comparisonRows.map((row) => {
              const leftVal = row.left ?? "—";
              const rightVal = row.right;
              const differs =
                rightVal != null &&
                leftVal.toLowerCase().trim() !== rightVal.toLowerCase().trim();
              return (
                <div
                  key={row.key}
                  className="grid grid-cols-[140px_1fr_1fr]"
                  style={{
                    borderBottom: "1px solid #D5DADC",
                    background: differs ? "rgba(12,152,208,0.04)" : "#fff",
                  }}
                >
                  <div
                    className="px-4 py-3 flex items-center gap-2"
                    style={{ background: "#FAFBFB" }}
                  >
                    <span
                      className="text-[11px] font-semibold uppercase tracking-widest"
                      style={{ color: "#556C75" }}
                    >
                      {row.label}
                    </span>
                    {differs && (
                      <span
                        className="w-1.5 h-1.5 rounded-full shrink-0"
                        style={{ background: "#0C98D0" }}
                      />
                    )}
                  </div>
                  <div
                    className="px-4 py-3"
                    style={{
                      borderLeft: differs
                        ? "2px solid #0C98D0"
                        : "1px solid #D5DADC",
                    }}
                  >
                    <span
                      className="text-[13px]"
                      style={{
                        color: differs ? "#064C68" : "#162429",
                        fontWeight: differs ? 500 : 400,
                      }}
                    >
                      {leftVal}
                    </span>
                  </div>
                  <div
                    className="px-4 py-3"
                    style={{
                      borderLeft: differs
                        ? "2px solid rgba(12,152,208,0.4)"
                        : "1px solid #D5DADC",
                    }}
                  >
                    {rightVal == null || !hasGolden ? (
                      <span
                        className="text-[13px] italic"
                        style={{ color: "#8FA8B2" }}
                      >
                        —
                      </span>
                    ) : (
                      <span
                        className="text-[13px]"
                        style={{
                          color: differs ? "#0C98D0" : "#162429",
                          fontWeight: differs ? 500 : 400,
                        }}
                      >
                        {rightVal}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </>
        )}

        {Object.keys(fieldScores).length > 0 && (
          <div
            className="flex flex-wrap gap-2 px-4 py-3"
            style={{ borderTop: "1px solid #D5DADC", background: "#FAFBFB" }}
          >
            {Object.entries(fieldScores).map(([key, score]) => {
              const sc = scoreColor(score);
              return (
                <span
                  key={key}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium"
                  style={{
                    background: sc.bg,
                    color: sc.color,
                    border: `1px solid ${sc.color}22`,
                  }}
                >
                  {fieldLabel(key)} {score.toFixed(2)}
                </span>
              );
            })}
          </div>
        )}
      </div>

      {/* Diagnostic details (conflicts) — formatted key/value view of the
          pipeline's details payload */}
      {item.item_type === "conflict" &&
        item.details &&
        Object.keys(item.details).length > 0 && (
          <div
            className="mx-4 mb-3 rounded overflow-hidden"
            style={{ border: "1px solid #D5DADC", background: "#fff" }}
          >
            <div
              className="px-4 py-2.5"
              style={{
                borderBottom: "1px solid #D5DADC",
                background: "#F5F7F8",
              }}
            >
              <span
                className="text-[11px] font-semibold uppercase tracking-wide"
                style={{ color: "#556C75" }}
              >
                Diagnostic details
              </span>
            </div>
            {flattenDetails(item.details).map(({ key, value }) => (
              <div
                key={key}
                className="grid grid-cols-[200px_1fr]"
                style={{ borderBottom: "1px solid #EFF1F2" }}
              >
                <div className="px-4 py-2" style={{ background: "#FAFBFB" }}>
                  <span
                    className="text-[11px] font-medium"
                    style={{ color: "#556C75" }}
                  >
                    {fieldLabel(key.split(" · ").pop() ?? key)}
                    {key.includes(" · ") && (
                      <span style={{ color: "#8FA8B2" }}>
                        {" "}
                        ({key.split(" · ").slice(0, -1).join(" · ")})
                      </span>
                    )}
                  </span>
                </div>
                <div
                  className="px-4 py-2"
                  style={{ borderLeft: "1px solid #EFF1F2" }}
                >
                  <span className="text-[12px]" style={{ color: "#162429" }}>
                    {value}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

      {/* Web evidence (pre-mastering, when the pipeline found any) */}
      {evidence.length > 0 && (
        <div
          className="mx-4 mb-3 rounded overflow-hidden"
          style={{ border: "1px solid #D5DADC", background: "#fff" }}
        >
          <div
            className="px-4 py-2.5"
            style={{ borderBottom: "1px solid #D5DADC", background: "#F5F7F8" }}
          >
            <span
              className="text-[11px] font-semibold uppercase tracking-wide"
              style={{ color: "#556C75" }}
            >
              Web-verified evidence
            </span>
          </div>
          {evidence.map((ev) => (
            <div
              key={ev.side}
              className="px-4 py-3 flex flex-col gap-0.5"
              style={{ borderBottom: "1px solid #EFF1F2" }}
            >
              <div className="flex items-baseline gap-2">
                <span
                  className="text-[11px] font-semibold"
                  style={{ color: "#556C75" }}
                >
                  Record {ev.side}
                </span>
                <span className="text-[13px]" style={{ color: "#162429" }}>
                  {ev.web_name ?? "—"}
                </span>
              </div>
              {ev.web_address && (
                <span className="text-[12px]" style={{ color: "#556C75" }}>
                  {ev.web_address}
                </span>
              )}
              {ev.web_url && (
                <a
                  href={ev.web_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[12px] inline-flex items-center gap-1 text-[#06709B] hover:underline"
                >
                  <LinkOutlined style={{ fontSize: 11 }} />
                  {new URL(ev.web_url).hostname}
                </a>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Prior review decisions with rationale (golden pair reviews) */}
      {priorDecisions.length > 0 && (
        <div
          className="mx-4 mb-3 rounded overflow-hidden"
          style={{ border: "1px solid #D5DADC", background: "#fff" }}
        >
          <div
            className="px-4 py-2.5"
            style={{ borderBottom: "1px solid #D5DADC", background: "#F5F7F8" }}
          >
            <span
              className="text-[11px] font-semibold uppercase tracking-wide"
              style={{ color: "#556C75" }}
            >
              Prior review decisions
            </span>
          </div>
          {priorDecisions.map((d, i) => (
            <div
              key={i}
              className="px-4 py-3 flex flex-col gap-1"
              style={{ borderBottom: "1px solid #EFF1F2" }}
            >
              <div className="flex items-baseline gap-2 flex-wrap">
                {d.decision && (
                  <span
                    className="text-[11px] font-medium px-1.5 py-0.5 rounded capitalize"
                    style={{
                      background:
                        d.decision === "merged" ? "#DCFCE7" : "#E8EEF2",
                      color: d.decision === "merged" ? "#15803D" : "#556C75",
                    }}
                  >
                    {d.decision.replace(/_/g, " ")}
                  </span>
                )}
                {d.actor && (
                  <span className="text-[11px]" style={{ color: "#556C75" }}>
                    by {d.actor}
                  </span>
                )}
                {d.at && (
                  <span
                    className="text-[11px] ml-auto shrink-0"
                    style={{ color: "#C4CDD0" }}
                  >
                    {fmt(d.at)}
                  </span>
                )}
              </div>
              <span
                className="text-[12px] leading-relaxed"
                style={{ color: "#2B4752" }}
              >
                {d.reason}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Sibling candidate pairs (PI dedup) */}
      {siblings.length > 0 && (
        <div
          className="mx-4 mb-3 rounded overflow-hidden"
          style={{ border: "1px solid #D5DADC", background: "#fff" }}
        >
          <div
            className="px-4 py-2.5"
            style={{ borderBottom: "1px solid #D5DADC", background: "#F5F7F8" }}
          >
            <span
              className="text-[11px] font-semibold uppercase tracking-wide"
              style={{ color: "#556C75" }}
            >
              {siblings.length} more pending candidate
              {siblings.length !== 1 ? "s" : ""} for this person
            </span>
          </div>
          {siblings.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => onOpenItem(s.id)}
              className="w-full text-left px-4 py-2.5 flex items-center justify-between bg-transparent border-0 cursor-pointer hover:bg-[#F5F7F8] transition-colors"
              style={{ borderBottom: "1px solid #EFF1F2" }}
            >
              <span className="text-[13px] text-[#06709B]">{s.label}</span>
              {s.confidence_pct != null && (
                <span className="text-[12px] font-medium text-[#556C75]">
                  {s.confidence_pct}%
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const signalsTab = (
    <div
      className="overflow-y-auto px-4 pt-4 pb-4"
      style={{ background: "#EFF1F2", height: "100%" }}
    >
      <div
        className="rounded overflow-hidden"
        style={{ border: "1px solid #D5DADC", background: "#fff" }}
      >
        <div
          className="px-4 py-2.5 flex items-center justify-between"
          style={{ borderBottom: "1px solid #D5DADC", background: "#F5F7F8" }}
        >
          <span
            className="text-[11px] font-semibold uppercase tracking-wide"
            style={{ color: "#556C75" }}
          >
            Signal breakdown
          </span>
          {item.weighted_score != null && (
            <span
              className="text-[12px] font-medium"
              style={{ color: "#064C68" }}
            >
              Weighted score {item.weighted_score.toFixed(2)}
            </span>
          )}
        </div>
        <div className="px-4 py-3 flex flex-col gap-2">
          {ALL_SIGNALS.map(({ key, label }) => {
            const score = fieldScores[key];
            const present = score != null;
            const sc = present ? scoreColor(score) : null;
            return (
              <div
                key={key}
                className="grid grid-cols-[160px_1fr_44px] items-center gap-3"
              >
                <span className="text-[12px]" style={{ color: "#556C75" }}>
                  {label}
                </span>
                <div
                  className="h-[5px] rounded overflow-hidden"
                  style={{ background: "#EFF1F2" }}
                >
                  {present && (
                    <div
                      className="h-full rounded"
                      style={{
                        width: `${Math.round(score * 100)}%`,
                        background: sc!.color,
                      }}
                    />
                  )}
                </div>
                <span
                  className="text-[12px] font-medium text-right tabular-nums"
                  style={{ color: present ? sc!.color : "#C4CDD0" }}
                >
                  {present ? score.toFixed(2) : "—"}
                </span>
              </div>
            );
          })}
        </div>
        <div
          className="px-4 py-2.5 text-[11px]"
          style={{
            borderTop: "1px solid #D5DADC",
            background: "#FAFBFB",
            color: "#8FA8B2",
          }}
        >
          {Object.keys(fieldScores).length} of {ALL_SIGNALS.length} signals
          present — absent signals were missing from the source data.
          {item.accept_basket && <> Basket: {item.accept_basket}.</>}
        </div>
      </div>
    </div>
  );

  const memberColumns: Array<{ key: keyof ComparisonMember; label: string }> =
    comparison?.entity === "investigator"
      ? [
          { key: "name", label: "Name" },
          { key: "institution", label: "Affiliation" },
          { key: "specialty", label: "Specialty" },
          { key: "npi", label: "NPI" },
          { key: "role", label: "Role" },
        ]
      : [
          { key: "name", label: "Name as registered" },
          { key: "address", label: "Address" },
          { key: "external_org_id", label: "Org ID" },
          { key: "pi_name", label: "PI" },
          { key: "phone", label: "Phone" },
        ];

  const tierColor = (tier?: string | null): string =>
    tier === "high" ? "#2E7D32" : tier === "medium" ? "#B45309" : "#8FA8B2";

  const membersTab = (
    <div
      className="overflow-y-auto px-4 pt-4 pb-4"
      style={{ background: "#EFF1F2", height: "100%" }}
    >
      {comparison?.mode === "single" && (
        <p className="text-[12px] mb-3" style={{ color: "#556C75" }}>
          All {totalMembers} source record{totalMembers === 1 ? "" : "s"} merged
          into this golden record — review every contributing site before
          splitting or dismissing.
        </p>
      )}

      {comparisonSides.map((side) => {
        const g = side.golden;
        return (
          <div
            key={side.key}
            className="mb-4 rounded overflow-hidden"
            style={{ border: "1px solid #D5DADC", background: "#fff" }}
          >
            <div
              className="px-4 py-2.5 flex items-center justify-between gap-2 flex-wrap"
              style={{
                borderBottom: "1px solid #D5DADC",
                background: "#F5F7F8",
              }}
            >
              <span
                className="text-[11px] font-semibold uppercase tracking-wide"
                style={{ color: "#064C68" }}
              >
                {side.label} · {side.members.length} record
                {side.members.length === 1 ? "" : "s"}
              </span>
              <div className="flex items-center gap-1.5 flex-wrap">
                {g?.admission_tier && (
                  <Tag
                    style={{
                      fontSize: 10,
                      marginInlineEnd: 0,
                      color: "#064C68",
                      background: "#E3F2F9",
                      border: "1px solid #C5E4F0",
                    }}
                  >
                    {g.admission_tier}
                  </Tag>
                )}
                {g?.recon_status && g.recon_status !== "AGREE" && (
                  <Tag
                    style={{
                      fontSize: 10,
                      marginInlineEnd: 0,
                      color: "#B45309",
                      background: "#FEF3C7",
                      border: "1px solid #FCE3A8",
                    }}
                  >
                    {g.recon_status}
                  </Tag>
                )}
                {g?.overall_confidence != null && (
                  <span
                    className="text-[11px] font-medium tabular-nums"
                    style={{ color: "#556C75" }}
                  >
                    {Math.round(g.overall_confidence * 100)}% confidence
                  </span>
                )}
              </div>
            </div>

            {g && (
              <div
                className="px-4 py-2 flex items-center gap-2 text-[12px] flex-wrap"
                style={{
                  borderBottom: "1px solid #EAECEE",
                  background: "#F7FBFD",
                }}
              >
                <span
                  className="text-[10px] font-semibold uppercase tracking-widest shrink-0"
                  style={{ color: "#064C68" }}
                >
                  Elected
                </span>
                <span className="font-medium" style={{ color: "#17242C" }}>
                  {g.name ?? "—"}
                </span>
                {comparison?.entity === "site" && g.address && (
                  <span style={{ color: "#556C75" }}>· {g.address}</span>
                )}
                {comparison?.entity === "investigator" && g.institution && (
                  <span style={{ color: "#556C75" }}>· {g.institution}</span>
                )}
                {g.golden_institution_id && (
                  <span style={{ color: "#8FA8B2" }}>
                    · inst {g.golden_institution_id}
                  </span>
                )}
              </div>
            )}

            <div className="overflow-x-auto">
              <div style={{ minWidth: 700 }}>
                <div
                  className="flex items-center px-4 py-2 gap-3"
                  style={{
                    borderBottom: "1px solid #EAECEE",
                    background: "#FAFBFB",
                  }}
                >
                  <span
                    className="shrink-0 text-[11px] font-semibold uppercase tracking-wide"
                    style={{ width: 92, color: "#556C75" }}
                  >
                    Source
                  </span>
                  {memberColumns.map((c) => (
                    <span
                      key={c.key}
                      className="flex-1 text-[11px] font-semibold uppercase tracking-wide"
                      style={{ color: "#556C75", minWidth: 0 }}
                    >
                      {c.label}
                    </span>
                  ))}
                  <span
                    className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-right"
                    style={{ width: 64, color: "#556C75" }}
                  >
                    Conf.
                  </span>
                </div>

                {side.members.map((m) => (
                  <div
                    key={m.id}
                    className="flex items-center px-4 py-2.5 gap-3"
                    style={{ borderBottom: "1px solid #F0F2F3" }}
                  >
                    <span className="shrink-0" style={{ width: 92 }}>
                      <Tag
                        style={{
                          fontSize: 10,
                          marginInlineEnd: 0,
                          color: "#48636F",
                          background: "#EEF2F4",
                          border: "1px solid #DDE4E7",
                        }}
                      >
                        {m.source_label || "—"}
                      </Tag>
                    </span>
                    {memberColumns.map((c) => {
                      const raw = m[c.key];
                      const val = raw == null || raw === "" ? "—" : String(raw);
                      return (
                        <span
                          key={c.key}
                          className="flex-1 text-[12px]"
                          style={{ color: "#17242C", minWidth: 0 }}
                        >
                          <span className="block truncate" title={val}>
                            {val}
                          </span>
                        </span>
                      );
                    })}
                    <span
                      className="shrink-0 text-right tabular-nums text-[12px] font-medium"
                      style={{ width: 64, color: tierColor(m.confidence_tier) }}
                    >
                      {m.confidence_pct != null ? `${m.confidence_pct}%` : "—"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })}

      {comparison?.mode === "single" && (
        <div className="flex flex-col gap-3">
          {comparison.conflicting_identifiers != null &&
            comparison.conflicting_identifiers.length > 0 && (
              <div
                className="rounded px-4 py-3"
                style={{ border: "1px solid #FCE3A8", background: "#FFFBEB" }}
              >
                <div
                  className="text-[11px] font-semibold uppercase tracking-wide mb-1.5"
                  style={{ color: "#B45309" }}
                >
                  Conflicting across members
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {comparison.conflicting_identifiers.map((f) => (
                    <Tag
                      key={f}
                      style={{
                        fontSize: 11,
                        marginInlineEnd: 0,
                        color: "#B45309",
                        background: "#FEF3C7",
                        border: "1px solid #FCE3A8",
                      }}
                    >
                      {fieldLabel(f)}
                    </Tag>
                  ))}
                </div>
              </div>
            )}

          {comparison.shared_identifiers != null &&
            Object.keys(comparison.shared_identifiers).length > 0 && (
              <div
                className="rounded px-4 py-3"
                style={{ border: "1px solid #CDEACD", background: "#F3FAF3" }}
              >
                <div
                  className="text-[11px] font-semibold uppercase tracking-wide mb-1.5"
                  style={{ color: "#2E7D32" }}
                >
                  Shared across all members
                </div>
                <div className="flex flex-col gap-1">
                  {Object.entries(comparison.shared_identifiers).map(
                    ([f, v]) => (
                      <div key={f} className="text-[12px]">
                        <span style={{ color: "#556C75" }}>
                          {fieldLabel(f)}:{" "}
                        </span>
                        <span style={{ color: "#17242C" }}>{v}</span>
                      </div>
                    ),
                  )}
                </div>
              </div>
            )}

          {comparison.aliases != null && comparison.aliases.length > 0 && (
            <div
              className="rounded px-4 py-3"
              style={{ border: "1px solid #D5DADC", background: "#fff" }}
            >
              <div
                className="text-[11px] font-semibold uppercase tracking-wide mb-1.5"
                style={{ color: "#556C75" }}
              >
                Name variants ({comparison.aliases.length})
              </div>
              <div className="flex flex-wrap gap-1.5">
                {comparison.aliases.map((a, i) => (
                  <Tag
                    key={`${a}-${i}`}
                    style={{
                      fontSize: 11,
                      marginInlineEnd: 0,
                      color: "#48636F",
                      background: "#EEF2F4",
                      border: "1px solid #DDE4E7",
                    }}
                  >
                    {a}
                  </Tag>
                ))}
              </div>
            </div>
          )}

          {comparison.evidence != null && comparison.evidence.length > 0 && (
            <div
              className="rounded px-4 py-3"
              style={{ border: "1px solid #F3C6C6", background: "#FEF6F6" }}
            >
              <div
                className="text-[11px] font-semibold uppercase tracking-wide mb-1.5"
                style={{ color: "#C2410C" }}
              >
                Split-risk evidence ({comparison.evidence.length})
              </div>
              <div className="flex flex-col gap-1">
                {comparison.evidence.map((e, i) => (
                  <div key={`${e.raw_site_id}-${i}`} className="text-[12px]">
                    <span style={{ color: "#17242C" }}>
                      {e.raw_site_name ?? e.raw_site_id ?? "—"}
                    </span>
                    {e.ev_name && (
                      <span style={{ color: "#8FA8B2" }}> · {e.ev_name}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const notesTab = (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        background: "#EFF1F2",
      }}
    >
      <div className="overflow-y-auto" style={{ flex: 1, minHeight: 0 }}>
        {comments.length === 0 ? (
          <p
            className="m-0 px-4 pt-4 text-[13px] italic"
            style={{ color: "#8FA8B2" }}
          >
            No notes yet.
          </p>
        ) : (
          <div className="px-4 pt-4 flex flex-col gap-2">
            {comments.map((c) => (
              <div key={c.id} className="flex gap-3">
                <div className="shrink-0 mt-0.5">
                  <AvatarWithInitials
                    name={c.author}
                    avatarSize={26}
                    avatarFontSize={10}
                    showLabel={false}
                  />
                </div>
                <div className="flex-1 min-w-0 pb-2">
                  <div className="flex items-baseline gap-2 mb-1">
                    <span
                      className="text-[12px] font-semibold"
                      style={{ color: "#162429" }}
                    >
                      {c.author}
                    </span>
                    <span
                      className="text-[11px] ml-auto shrink-0"
                      style={{ color: "#C4CDD0" }}
                    >
                      {new Date(c.timestamp).toLocaleDateString()}
                    </span>
                  </div>
                  <div
                    className="text-[12px] leading-relaxed rounded px-3 py-1.5"
                    style={{
                      border: "1px solid #D5DADC",
                      background: "#F5F7F8",
                      color: "#2B4752",
                    }}
                  >
                    {renderText(c.text)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div
        className="flex gap-3 items-start px-4 py-4 shrink-0"
        style={{ borderTop: "1px solid #D5DADC", background: "#fff" }}
      >
        <div className="shrink-0 mt-1">
          <AvatarWithInitials
            name={userFullName || "User"}
            avatarSize={28}
            avatarFontSize={11}
            showLabel={false}
          />
        </div>
        <div className="flex-1 relative">
          <TextArea
            value={draft}
            onChange={(e) => onDraftChange(e.target.value)}
            placeholder="Add a note…"
            autoSize={{ minRows: 2, maxRows: 4 }}
            style={{ fontSize: 13, paddingRight: 36, resize: "none" }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey))
                onSubmitComment();
            }}
          />
          <button
            type="button"
            disabled={!draft.trim()}
            onClick={onSubmitComment}
            className="absolute bottom-2 right-2.5 transition-colors border-0 bg-transparent"
            style={{ color: draft.trim() ? "#0C98D0" : "#C4CDD0" }}
          >
            <SendOutlined style={{ fontSize: 13 }} />
          </button>
        </div>
      </div>
    </div>
  );

  const activityTab = (
    <div
      className="overflow-y-auto px-4 pt-4 pb-4"
      style={{ background: "#EFF1F2", height: "100%" }}
    >
      <div
        className="rounded overflow-hidden"
        style={{ border: "1px solid #D5DADC", background: "#fff" }}
      >
        <div className="px-4 py-3">
          {auditHistoryLoading ? (
            <div className="flex justify-center py-4">
              <Spin size="small" />
            </div>
          ) : auditHistory.length === 0 ? (
            <p className="m-0 text-[13px] italic" style={{ color: "#8FA8B2" }}>
              No activity recorded yet.
            </p>
          ) : (
            <div className="flex flex-col">
              {auditHistory.map((entry, i) => {
                const isLast = i === auditHistory.length - 1;
                return (
                  <div
                    key={`${entry.performed_at}-${i}`}
                    className="flex gap-3"
                  >
                    <div
                      className="flex flex-col items-center shrink-0"
                      style={{ width: 20 }}
                    >
                      <div
                        className="rounded-full shrink-0"
                        style={{
                          width: 8,
                          height: 8,
                          marginTop: 6,
                          background: "#0C98D0",
                        }}
                      />
                      {!isLast && (
                        <div
                          style={{
                            flex: 1,
                            width: 1,
                            background: "#D5DADC",
                            minHeight: 16,
                          }}
                        />
                      )}
                    </div>
                    <div className="pb-3 min-w-0 flex-1">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span
                          className="text-[13px] font-medium"
                          style={{ color: "#162429" }}
                        >
                          {ACTION_LABELS[entry.action] ?? entry.action}
                        </span>
                        {entry.action_value && (
                          <span
                            className="text-[11px] px-1.5 py-0.5 rounded"
                            style={{ background: "#D2EFF9", color: "#06709B" }}
                          >
                            {entry.action_value}
                          </span>
                        )}
                        {entry.writeback_status === "applied" && (
                          <span
                            className="text-[11px] px-1.5 py-0.5 rounded"
                            style={{ background: "#DCFCE7", color: "#15803D" }}
                            title={`Decision written to ${entry.writeback_target}`}
                          >
                            → pipeline
                          </span>
                        )}
                        {entry.writeback_status === "applied_in_run" && (
                          <span
                            className="text-[11px] px-1.5 py-0.5 rounded"
                            style={{ background: "#DCFCE7", color: "#15803D" }}
                            title={`Confirmed via ${entry.writeback_target}`}
                          >
                            {entry.writeback_detail
                              ? `run ${entry.writeback_detail}`
                              : "applied"}
                          </span>
                        )}
                        {entry.writeback_status === "failed" && (
                          <span
                            className="text-[11px] px-1.5 py-0.5 rounded"
                            style={{ background: "#FEE2E2", color: "#BE0303" }}
                            title={entry.writeback_detail || "Writeback failed"}
                          >
                            pipeline sync failed
                          </span>
                        )}
                        <span
                          className="text-[11px] ml-auto shrink-0"
                          style={{ color: "#C4CDD0" }}
                        >
                          {fmt(entry.performed_at)}
                        </span>
                      </div>
                      {entry.performed_by && (
                        <div
                          className="text-[12px]"
                          style={{ color: "#556C75" }}
                        >
                          by {entry.performed_by}
                        </div>
                      )}
                      {entry.notes && (
                        <div
                          className="mt-1 text-[12px] leading-relaxed px-3 py-2 rounded"
                          style={{
                            background: "#F5F7F8",
                            color: "#2B4752",
                            border: "1px solid #D5DADC",
                          }}
                        >
                          {entry.notes}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const tabItems = [
    { key: "details", label: "Details", children: detailsTab },
    ...(showMembersTab
      ? [
          {
            key: "members",
            label: `Member records (${totalMembers})`,
            children: membersTab,
          },
        ]
      : []),
    ...(showSignalsTab
      ? [{ key: "signals", label: "Signals", children: signalsTab }]
      : []),
    {
      key: "notes",
      label: `Notes${comments.length > 0 ? ` (${comments.length})` : ""}`,
      children: notesTab,
    },
    {
      key: "activity",
      label: `Activity${auditHistory.length > 0 ? ` (${auditHistory.length})` : ""}`,
      children: activityTab,
    },
  ];

  return (
    <Modal
      open
      onCancel={onClose}
      footer={null}
      width="min(92vw, 960px)"
      centered
      closeIcon={null}
      styles={{
        body: {
          padding: 0,
          display: "flex",
          flexDirection: "column",
          height: "88vh",
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <div
        className="flex items-start justify-between px-5 py-4 shrink-0"
        style={{
          background: "linear-gradient(180deg, #17242C 0%, #094B68 100%)",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <div className="flex flex-col gap-1.5 min-w-0">
          <div className="flex items-center gap-2.5 flex-wrap">
            <span className="text-[17px] font-semibold text-white">
              {item.entity_display_name}
            </span>
            <Tag
              style={{
                color: colors.color,
                background: "rgba(255,255,255,0.1)",
                border: "1px solid rgba(255,255,255,0.16)",
                fontSize: 11,
                fontWeight: 500,
                marginInlineEnd: 0,
              }}
            >
              {ISSUE_TYPE_LABELS[normalizedType] ?? item.issue_type}
            </Tag>
            <Tag
              style={{
                color: "#C4CDD0",
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.12)",
                fontSize: 11,
                marginInlineEnd: 0,
              }}
            >
              {ITEM_TYPE_LABELS[item.item_type]}
            </Tag>
            {item.accept_basket && (
              <Tag
                style={{
                  color: "#8FD4F0",
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  fontSize: 11,
                  marginInlineEnd: 0,
                }}
              >
                {item.accept_basket}
              </Tag>
            )}
            {awaitingRun && (
              <Tag
                style={{
                  color: "#FAC775",
                  background: "rgba(250,199,117,0.12)",
                  border: "1px solid rgba(250,199,117,0.3)",
                  fontSize: 11,
                  marginInlineEnd: 0,
                }}
              >
                <ClockCircleOutlined style={{ fontSize: 10, marginRight: 4 }} />
                Awaiting mastering run
              </Tag>
            )}
            {pipelineApplied && (
              <Tag
                style={{
                  color: "#9FE1CB",
                  background: "rgba(159,225,203,0.12)",
                  border: "1px solid rgba(159,225,203,0.3)",
                  fontSize: 11,
                  marginInlineEnd: 0,
                }}
              >
                Applied
                {pipelineApplied.writeback_detail
                  ? ` in ${pipelineApplied.writeback_detail}`
                  : " by pipeline"}
              </Tag>
            )}
          </div>
          <div
            className="text-[12px] flex items-center gap-1.5 flex-wrap"
            style={{ color: "#C4CDD0" }}
          >
            {item.confidence_pct != null && (
              <span>
                {item.confidence_pct}% confidence
                {item.confidence_lower != null &&
                  item.confidence_upper != null && (
                    <>
                      {" "}
                      (CI {Math.round(item.confidence_lower * 100)}–
                      {Math.round(item.confidence_upper * 100)})
                    </>
                  )}
              </span>
            )}
            {item.created_at && (
              <>
                {item.confidence_pct != null && <span>·</span>}
                <span>Opened {fmt(item.created_at)}</span>
              </>
            )}
            {item.assigned_to && (
              <>
                <span>·</span>
                <span>Assigned: {item.assigned_to}</span>
              </>
            )}
            {item.due_date && (
              <>
                <span>·</span>
                <span
                  className="inline-flex items-center gap-1"
                  style={{ color: overdue ? "#F09595" : "#C4CDD0" }}
                >
                  <ClockCircleOutlined style={{ fontSize: 11 }} />
                  {overdue
                    ? `Overdue since ${fmt(item.due_date)}`
                    : `Due ${fmt(item.due_date)}`}
                </span>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 ml-4 shrink-0">
          {graphHref && (
            <Tooltip title="Open this record's match neighborhood in the mastery graph">
              <a
                href={graphHref}
                className="flex h-7 items-center gap-1.5 rounded px-2 text-xs"
                style={{
                  color: "#8FD4F0",
                  border: "1px solid rgba(255,255,255,0.16)",
                }}
              >
                <ShareAltOutlined style={{ fontSize: 12 }} />
                View in graph
              </a>
            </Tooltip>
          )}
          <button
            type="button"
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded transition-colors border-0 bg-transparent"
            style={{ color: "#C4CDD0" }}
          >
            <CloseOutlined style={{ fontSize: 12 }} />
          </button>
        </div>
      </div>

      {/* Priority & Status controls */}
      <div
        className="flex items-center gap-4 px-5 py-3 border-b shrink-0"
        style={{ background: "#ffffff", borderColor: "#D5DADC" }}
      >
        <div className="flex items-center gap-2">
          <span
            className="text-[12px] font-medium"
            style={{ color: "#064C68" }}
          >
            Priority
          </span>
          <Select
            size="small"
            value={item.priority ?? undefined}
            placeholder="Set priority"
            allowClear
            disabled={!canSteward}
            style={{ width: 130 }}
            options={[
              { value: "high", label: "High" },
              { value: "medium", label: "Medium" },
              { value: "low", label: "Low" },
            ]}
            onChange={onPriorityChange}
          />
        </div>
        <div className="flex items-center gap-2">
          <span
            className="text-[12px] font-medium"
            style={{ color: "#064C68" }}
          >
            Status
          </span>
          {(() => {
            const badge =
              STATUS_BADGES[item.status ?? "pending"] ?? STATUS_BADGES.pending;
            return (
              <span
                className="inline-block text-[11px] font-medium rounded px-2 py-0.5"
                style={{ backgroundColor: badge.bg, color: badge.text }}
              >
                {badge.label}
              </span>
            );
          })()}
        </div>
        {canSteward &&
          DECIDED_STATUSES.has(item.status ?? "") &&
          !pipelineApplied && (
            <Tooltip title="Revert this decision — the item returns to the queue and the recorded verdict is retracted before the next mastering run.">
              <button
                type="button"
                onClick={() => onAction("reopened", { keepOpen: true })}
                className="px-2.5 py-1 text-[12px] rounded border border-[#D5DADC] bg-white hover:bg-[#F5F7F8] transition-colors"
              >
                Reopen
              </button>
            </Tooltip>
          )}
      </div>

      {/* Pinned summary — what the automated pipeline observed (why this item
          is in the queue). Lives outside the scroll area so it stays visible. */}
      {item.issue_summary && (
        <div
          className="px-5 py-2.5 shrink-0 flex items-baseline gap-3"
          style={{ background: "#F5F7F8", borderBottom: "1px solid #D5DADC" }}
        >
          <span
            className="text-[11px] font-semibold uppercase tracking-wide shrink-0"
            style={{ color: "#556C75" }}
          >
            Summary
          </span>
          <span
            className="text-[13px] leading-snug"
            style={{ color: "#162429" }}
          >
            {item.issue_summary}
          </span>
        </div>
      )}

      {/* Tabbed content — the arbitrary-selector classes stretch antd's inner
          content holder so each tab pane fills the modal height and its own
          overflow-y-auto root can actually scroll */}
      <Tabs
        defaultActiveKey="details"
        size="small"
        className="[&_.ant-tabs-content-holder]:flex-1 [&_.ant-tabs-content-holder]:min-h-0 [&_.ant-tabs-content-holder]:overflow-hidden [&_.ant-tabs-content]:h-full [&_.ant-tabs-tabpane]:h-full"
        style={{
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
        tabBarStyle={{
          margin: 0,
          paddingLeft: 16,
          paddingRight: 16,
          background: "#fff",
          borderBottom: "1px solid #D5DADC",
          flexShrink: 0,
        }}
        items={tabItems}
      />

      {/* Footer — actions depend on item type; gated by steward role (BR 59) */}
      <div
        className="flex items-center justify-end gap-2 px-5 py-3 shrink-0"
        style={{ borderTop: "1px solid #D5DADC", background: "#fff" }}
      >
        {canSteward ? (
          FOOTER_ACTIONS[item.item_type].map(({ action, label, kind }) => (
            <button
              key={action}
              type="button"
              onClick={() =>
                action === "skip"
                  ? onClose()
                  : onAction(action as MasteryAction)
              }
              className={FOOTER_BUTTON_CLASSES[kind]}
            >
              {kind === "primary" && <CheckOutlined style={{ fontSize: 11 }} />}
              {label}
            </button>
          ))
        ) : (
          <span
            className="text-[12px] italic mr-auto"
            style={{ color: "#8FA8B2" }}
          >
            Read-only — a data steward role is required to action this item. You
            can still add notes.
          </span>
        )}
      </div>
    </Modal>
  );
}
